import React, {useCallback, useEffect, useRef, useState} from 'react'
import styled from 'styled-components'
import moment from 'moment'
import random from 'lodash/random'
import sample from 'lodash/sample'
import uniq from 'lodash/uniq'
import PageHeaderButton from 'lib/ui/PageHeader/PageHeaderButton'
import {colors, spacing} from 'lib/ui/theme'
import {api} from 'lib/url'
import SelectDemoEmojisDialog from 'organization/Event/EmojiPage/Demo/SelectDemoEmojisDialog'
import {DEFAULT_EMOJIS} from 'Event/Dashboard/components/EmojiList/emoji'
import {useEvent} from 'Event/EventProvider'
import {useTemplate} from 'Event/TemplateProvider'
import {Template} from 'Event/template'
import {emojisFromSidebar} from 'Event/template/converter'
import {useOrganization} from 'organization/OrganizationProvider'

const MIN_INTERVAL_MS = 50
const MAX_INTERVAL_MS = 500

// A duration of 0 minutes means the demo runs indefinitely.
const DEMO_DURATIONS = [
  {label: '1m', minutes: 1},
  {label: '5m', minutes: 5},
  {label: '10m', minutes: 10},
  {label: '∞', minutes: 0},
]

export default function DemoButton() {
  const containerRef = useRef<HTMLDivElement>(null)
  const {
    availableEmojis,
    started,
    isInfinite,
    remainingSeconds,
    start,
    stop,
  } = useEmojiDemo()
  const [showDurations, setShowDurations] = useState(false)
  const [pendingDuration, setPendingDuration] = useState<number | null>(null)

  const closeDurations = () => {
    setShowDurations(false)
  }

  const toggleDurations = () => {
    setShowDurations((current) => !current)
  }

  useEffect(() => {
    if (!showDurations) {
      return
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        closeDurations()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showDurations])

  const selectDuration = (minutes: number) => {
    closeDurations()
    setPendingDuration(minutes)
  }

  const closeEmojiDialog = () => {
    setPendingDuration(null)
  }

  const confirmEmojis = (selected: string[]) => {
    if (pendingDuration === null) {
      return
    }

    start(pendingDuration, selected)
    setPendingDuration(null)
  }

  if (started) {
    const text = isInfinite
      ? 'Stop Demo (∞)'
      : `Stop Demo (${moment.utc(remainingSeconds * 1000).format('m:ss')})`

    return <StopButton onClick={stop} text={text} />
  }

  return (
    <>
      <Container ref={containerRef}>
        <StartButton
          onClick={toggleDurations}
          disabled={availableEmojis.length === 0}
        />
        <DurationRow visible={showDurations}>
          {DEMO_DURATIONS.map((duration) => (
            <PageHeaderButton
              key={duration.label}
              text={duration.label}
              aria-label={`demo duration ${duration.minutes} minutes`}
              variant="contained"
              color="warning"
              onClick={() => selectDuration(duration.minutes)}
            />
          ))}
        </DurationRow>
      </Container>
      <SelectDemoEmojisDialog
        open={pendingDuration !== null}
        availableEmojis={availableEmojis}
        onClose={closeEmojiDialog}
        onConfirm={confirmEmojis}
      />
    </>
  )
}

function StartButton(props: {onClick: () => void; disabled?: boolean}) {
  return (
    <PageHeaderButton
      text="Start Demo"
      variant="outlined"
      color="primary"
      aria-label="start demo"
      onClick={props.onClick}
      disabled={props.disabled}
    />
  )
}

function StopButton(props: {onClick: () => void; text: string}) {
  return (
    <PageHeaderButton
      text={props.text}
      variant="contained"
      color="warning"
      aria-label="stop demo"
      onClick={props.onClick}
    />
  )
}

function getEventEmojiNames(template: Template): string[] {
  if ('emojiList' in template && template.emojiList.emojis.length > 0) {
    return uniq(template.emojiList.emojis)
  }

  const fromSidebar = emojisFromSidebar(template)
  if (fromSidebar.length > 0) {
    return uniq(fromSidebar)
  }

  return uniq(DEFAULT_EMOJIS.map((emoji) => emoji.name))
}

function useEmojiDemo() {
  const template = useTemplate()
  const availableEmojis = getEventEmojiNames(template)
  const [started, setStarted] = useState(false)
  const [remainingSeconds, setRemainingSeconds] = useState(0)
  // A duration of 0 minutes means the demo runs indefinitely.
  const [durationMinutes, setDurationMinutes] = useState(0)
  const [emojis, setEmojis] = useState<string[]>([])
  const sendEmoji = useSendRandomEmoji(emojis)
  const isInfinite = started && durationMinutes === 0

  const stop = useCallback(() => {
    setStarted(false)
    setEmojis([])
    setRemainingSeconds(0)
    setDurationMinutes(0)
  }, [])

  useEffect(() => {
    if (!started || !emojis.length) {
      return
    }

    let emojiTimer = 0

    const startLoop = () => {
      sendEmoji()
      emojiTimer = window.setTimeout(
        startLoop,
        random(MIN_INTERVAL_MS, MAX_INTERVAL_MS, false),
      )
    }

    startLoop()

    let remainingTimer = 0
    if (durationMinutes > 0) {
      const endTime = Date.now() + durationMinutes * 60 * 1000
      remainingTimer = handleRemainingTimer(endTime, setRemainingSeconds, stop)
    }

    return () => {
      clearTimeout(emojiTimer)
      if (remainingTimer) {
        clearInterval(remainingTimer)
      }
    }
  }, [started, emojis, sendEmoji, durationMinutes, stop])

  const start = (minutes: number, selectedEmojis: string[]) => {
    if (!selectedEmojis.length) {
      return
    }

    setDurationMinutes(minutes)
    setEmojis(selectedEmojis)
    setRemainingSeconds(minutes * 60)
    setStarted(true)
  }

  return {availableEmojis, started, isInfinite, remainingSeconds, start, stop}
}

function useSendRandomEmoji(emojis: string[]) {
  const {event} = useEvent()
  const {client} = useOrganization()

  return useCallback(async () => {
    const name = sample(emojis)
    if (!name) {
      return
    }

    const url = api(`/events/${event.id}/emoji_page`)

    try {
      await client.post(url, {name})
    } catch (error) {
      console.error(error)
    }
  }, [client, emojis, event.id])
}

function handleRemainingTimer(
  endTime: number,
  setRemainingSeconds: (remaining: number) => void,
  stop: () => void,
) {
  return window.setInterval(() => {
    const remaining = Math.max(0, Math.ceil((endTime - Date.now()) / 1000))
    setRemainingSeconds(remaining)

    if (remaining <= 0) {
      stop()
    }
  }, 1000)
}

const Container = styled.div`
  display: flex;
  align-items: center;
`

const DurationRow = styled.div<{visible: boolean}>`
  display: flex;
  align-items: center;
  gap: 6px;
  overflow: hidden;
  margin-left: ${(props) => (props.visible ? spacing[2] : 0)};
  padding-left: ${(props) => (props.visible ? spacing[2] : 0)};
  border-left: 1px solid
    ${(props) => (props.visible ? colors.gray300 : 'transparent')};
  max-width: ${(props) => (props.visible ? '210px' : '0')};
  opacity: ${(props) => (props.visible ? 1 : 0)};
  transition: max-width 0.2s ease-out, opacity 0.2s ease-out,
    margin-left 0.2s ease-out, padding-left 0.2s ease-out,
    border-color 0.2s ease-out;
`
